import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, CircleX, FolderOutput, Loader, MessageSquareShare, Search } from 'lucide-react';
import { debounce, exchangeDate, formatDate, getPractice, numberWithCommas, userMessageTemplates } from "../../../helpers";
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import { exportAllOutgoingMessage, fetchFidelisFilterData, fetchFidelisOutgoingMessage } from '../../../services/fidelisPatient';
import DatePicker from "react-datepicker";
import DataTablePagination from '../../DataTablePagination';
import Spacing from '../../Spacing';
import OutgoingFidelisMessageDataTable from '../../DataTable/OutgoingFidelisMessageDataTable';
import Filter from "../../Filter";

export default function OutgoingFidelisMessages({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [exportAllButtonLoader, setExportAllButtonLoader] = useState(false);
  const [messageTemplate] = useState(userMessageTemplates());
  const [practiceLocationsOptions, setPracticeLocationsOptions] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [dataLoading, setDataLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    account_id: account_id,
    location_id: location_id,
    start_date: null,
    end_date: null,
    search_term: "",
    status: "",
    template_id: null,
    practice_location: "",
    order_by: [],
  });
  const [patientFormData, setPatientFormData] = useState(filter);

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadFilterOptions(account_id, location_id);
  }, [account_id, location_id]);

  const loadFilterOptions = async (accountId, locationId) => {
    try {
      const response = await fetchFidelisFilterData(accountId, locationId);
      if (response) {
        const results = response?.data ?? [];
        
        const practiceLocationsOptions = results.practice_locations?.map((s) => ({ value: s, label: s }));
        setPracticeLocationsOptions(practiceLocationsOptions);
      }
    } catch (error) {
      // console.log("🚀 ~ loadFidelisFilterOptions ~ error:", error);
    }
  };

  useEffect(() => {
    loadData(filter);
  }, [filter]);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchFidelisOutgoingMessage(getFilter);
      if (response) {
        setData(response?.data?.results);
        setDataCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
      }
    } catch(error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleDateChange = (update) => {
    const [start, end] = update;

    if (start && end && formatDate(start) === formatDate(end)) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    } else if (start && end) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(end),
      });
    } else if (start) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    }

    setDateRange([start, end]);
  };

  const allMessageTemplate = [
    { value: "all", label: "All" },
    ...(messageTemplate?.map(option => ({
      value: option?.id,
      label: option?.title,
    })) || [])
  ];

  const allStatusOptions = [
    { value: "all", label: "All" },
    { value: "delivered", label: "Delivered" },
    { value: "failed", label: "Failed" },
    { value: "initialized", label: "Initialized" },
    { value: "insufficient_credits", label: "Insufficient Credits" },
    { value: "invalid_phone", label: "Invalid Phone" },
    { value: "queued", label: "Queued" },
    { value: "scheduled", label: "Scheduled" },
  ];

  const handleExportAll = async (e) => {
    setExportAllButtonLoader(true);
    try {
      const response = await exportAllOutgoingMessage(filter);

      if (response && response.data) {
        const responseData = response.data;
        const dataBlob = new Blob([responseData], {
          type: "text/csv;charset=utf-8;",
        });
        const generateURL = window.URL.createObjectURL(dataBlob);

        // File Name Generate
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const filename = `Outgoing_message_list_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.csv`;

        const link = document.createElement("a");
        link.href = generateURL;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        window.URL.revokeObjectURL(generateURL);
      }
      
    } catch (error) {
    } finally {
      setExportAllButtonLoader(false);
    }
  };

  useDocumentTitle("Outgoing Message");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: "#898E87" }}
          >
            <MessageSquareShare width={20} height={20} />
          </i>
          Outgoing Message
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings docpro_grid_gap_10">
          <div
            className={`flex items-center docpro_grid_gap_10 ${
              filter.start_date !== filter.end_date
                ? "docpro_have_date_filter2"
                : ""
            }`}
          >
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>

            <div className={`flex items-center docpro_grid_gap_4`}>
              <div
                className={`docpro_toggle_calendar docpro_tip_checkbox ${
                  filter.start_date ? "active" : ""
                }`}
              >
                <CalendarDays />
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={handleDateChange}
                />
                {/* <DatePicker
                  selected={
                    filter.start_date ? new Date(filter.start_date) : new Date()
                  }
                  onChange={(date) => handleDate(date)}
                /> */}
                {filter.start_date && filter.end_date && (
                  <>
                    <span>{exchangeDate(filter.start_date)}</span>
                    {filter.start_date !== filter.end_date && (
                      <span>{exchangeDate(filter.end_date)}</span>
                    )}
                  </>
                )}

                <div className="docpro_tip">
                  <span>Delivery date</span>
                </div>
              </div>
              {filter.start_date && filter.end_date && (
                <span
                  onClick={() => {
                    setFilter({
                      ...filter,
                      start_date: "",
                      end_date: "",
                    });
                    setDateRange([null, null]);
                    setDataLoading(true);
                  }}
                >
                  <CircleX
                    size={22}
                    color="var(--accent)"
                    className="pointer"
                  />
                </span>
              )}
            </div>
          </div>
          <div className='flex items-center docpro_grid_gap_10'>
            <button 
              className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
              style={{ whiteSpace: 'nowrap', padding: '6.5px 14px' }}
              onClick={handleExportAll}
              disabled = {exportAllButtonLoader ? true : false}
            >
              <FolderOutput width={20} height={20} />
              Export All
              {exportAllButtonLoader && (
                <span className="docpro_loading">
                  <Loader width={20} height={20} />
                </span>
              )}
            </button>

            <Filter
              filterType="fidelis_outgoing"
              reloadData={setFilter}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              practiceLocationsOptions={practiceLocationsOptions}
              allMessageTemplate={allMessageTemplate}
              allStatusOptions={allStatusOptions}
            />
          </div>
        </div>
        <OutgoingFidelisMessageDataTable
          tableData={data}
          reloadData={loadData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          messageTemplate={messageTemplate}
        />
        {(data.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>
    </>
  );
}